
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertTriangle, Check, Cloud, Code, Copy, Download, ExternalLink } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import GeneratedCodeDisplay from '@/components/dashboard/agent-builder/GeneratedCodeDisplay';
import generateAgentCode from '@/components/dashboard/agent-builder/AgentCodeGenerator';

interface DeploymentStepProps {
  agentData: any;
  userId: string;
}

const DeploymentStep: React.FC<DeploymentStepProps> = ({ agentData, userId }) => {
  const { toast } = useToast();
  const [generatedCode, setGeneratedCode] = useState<string>(agentData.generatedCode || '');
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>("code");
  const [deploymentStatus, setDeploymentStatus] = useState<'idle' | 'deploying' | 'success' | 'failed'>('idle');

  useEffect(() => {
    if (!generatedCode && agentData.name && agentData.template) {
      generateCode();
    }
  }, []);

  const generateCode = () => {
    setIsGenerating(true);

    // In a real implementation, this would call an API to generate the code
    setTimeout(() => {
      const code = generateAgentCode({
        selectedTemplate: agentData.template,
        agentName: agentData.name,
        agentSettings: agentData.templateSettings || {},
        uploadedDocuments: agentData.sopDocument ? [agentData.sopDocument] : [],
        userId,
        provider: agentData.llmService || 'openai'
      });

      setGeneratedCode(code);
      agentData.generatedCode = code;

      setIsGenerating(false);

      toast({
        title: "Code Generated",
        description: `Generated SOP agent code for "${agentData.name}"`
      });
    }, 2000);
  };

  const handleDeploy = () => {
    setDeploymentStatus('deploying');
    
    // In a real implementation, this would call an API to deploy the agent
    setTimeout(() => {
      setDeploymentStatus('success');
      
      toast({
        title: "Deployment Successful",
        description: `The "${agentData.name}" agent has been deployed and is now live`
      });
    }, 3000);
  };

  const copyCode = () => {
    navigator.clipboard.writeText(generatedCode);
    toast({
      title: "Code Copied",
      description: "Agent code has been copied to clipboard"
    });
  };

  const downloadCode = () => {
    const blob = new Blob([generatedCode], { type: 'text/javascript' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${agentData.name.replace(/\s+/g, '_')}_agent.js`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast({
      title: "Code Downloaded",
      description: "Agent code has been downloaded"
    });
  };

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Generated SOP Agent</h3>
        <p className="text-sm text-gray-600">
          Review and deploy your SOP agent with the generated code.
        </p>

        <Tabs defaultValue="code" value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="code" className="flex items-center gap-1">
              <Code className="h-4 w-4" />
              <span>Generated Code</span>
            </TabsTrigger>
            <TabsTrigger value="deploy" className="flex items-center gap-1">
              <Cloud className="h-4 w-4" />
              <span>Deployment</span>
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="code" className="space-y-4">
            {!generatedCode ? (
              <Button
                onClick={generateCode}
                disabled={isGenerating}
                className="w-full bg-[#0EA5E9] text-white hover:bg-[#0EA5E9]/90"
              >
                {isGenerating ? 'Generating...' : 'Generate Code'}
              </Button>
            ) : (
              <div className="space-y-4">
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    className="flex items-center gap-1"
                    onClick={copyCode}
                  >
                    <Copy className="h-4 w-4" />
                    <span>Copy</span>
                  </Button>
                  <Button
                    variant="outline"
                    className="flex items-center gap-1"
                    onClick={downloadCode}
                  >
                    <Download className="h-4 w-4" />
                    <span>Download</span>
                  </Button>
                </div>
                
                <GeneratedCodeDisplay generatedCode={generatedCode} />
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="deploy" className="space-y-4">
            <Card>
              <CardContent className="p-6">
                <h4 className="text-lg font-medium mb-4">Deployment Options</h4>
                
                <div className="space-y-6">
                  <div className="space-y-2">
                    <h5 className="font-medium">Google Cloud Platform (GCP)</h5>
                    <p className="text-sm text-gray-600">
                      Deploy your agent to Google Cloud Platform for scalability and reliability.
                    </p>
                    <Button
                      className="flex items-center gap-2 mt-2 bg-[#0EA5E9] text-white hover:bg-[#0EA5E9]/90"
                      disabled={!generatedCode || deploymentStatus === 'deploying'}
                      onClick={handleDeploy}
                    >
                      {deploymentStatus === 'idle' && (
                        <>
                          <Cloud className="h-4 w-4" />
                          <span>Deploy to GCP</span>
                        </>
                      )}
                      {deploymentStatus === 'deploying' && (
                        <>
                          <span className="animate-spin mr-2">⟳</span>
                          <span>Deploying...</span>
                        </>
                      )}
                      {deploymentStatus === 'success' && (
                        <>
                          <Check className="h-4 w-4" />
                          <span>Deployed Successfully</span>
                        </>
                      )}
                      {deploymentStatus === 'failed' && (
                        <>
                          <AlertTriangle className="h-4 w-4" />
                          <span>Deployment Failed</span>
                        </>
                      )}
                    </Button>
                  </div>
                  
                  {deploymentStatus === 'success' && (
                    <div className="bg-green-50 p-4 rounded-md">
                      <h5 className="font-medium text-green-800 flex items-center">
                        <Check className="h-4 w-4 mr-2" />
                        Deployment Successful
                      </h5>
                      <p className="text-sm text-green-700 mt-1">
                        Your SOP agent "{agentData.name}" has been deployed successfully.
                      </p>
                      <div className="mt-2">
                        <Button 
                          variant="outline" 
                          className="text-sm h-8 flex items-center gap-1"
                          onClick={() => window.open('#', '_blank')}
                        >
                          <span>View Deployment</span>
                          <ExternalLink className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  <div className="space-y-2 pt-4 border-t">
                    <h5 className="font-medium">Deployment Instructions</h5>
                    <div className="text-sm text-gray-600 space-y-2">
                      <p>To deploy your agent manually:</p>
                      <ol className="list-decimal pl-5 space-y-1">
                        <li>Download the generated code</li>
                        <li>Setup a new Cloud Function in GCP</li>
                        <li>Upload the code and configure environment variables</li>
                        <li>Set appropriate IAM permissions</li>
                        <li>Deploy and test the endpoint</li>
                      </ol>
                      <p className="mt-2">
                        For detailed instructions, refer to the GCP Cloud Functions documentation.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default DeploymentStep;
